import { Alert, Button, Stack } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

/**
 * BomUpdateBanner — S122/S148 (HU-07/CA7.1, CA7.5, RN-07.2): the notice that the library holds a
 * version of the linked BOM newer than the one this workflow uses.
 *
 * Deliberately NOT built on `AlertUI`: that helper hides itself after 5 s, and this notice must stay
 * on screen until the user acts on it (updates or dismisses it). Nothing changes just because the
 * notice is shown (RN-07.4): it offers two explicit actions and no more.
 *
 * @param {object} props
 * @param {object} props.link the `WorkflowBomLink` of the workflow (contrato-sun.md §2)
 * @param {object} props.update the version actually on offer — the highest APPROVED one above the
 * linked (and the dismissed) one, resolved by `bomApprovedUpdate.js`; `{}` when there is none
 * @param {function()} props.onUpdate opens the impact dialog (N19 → N20)
 * @param {function()} props.onDismiss records the dismissal (N18)
 * @param {boolean} props.busy whether an operation started from the banner is still running
 */
const BomUpdateBanner = ({ link = {}, update = {}, onUpdate = () => {}, onDismiss = () => {}, busy = false }) => {
  const { t_ } = useSmartTranslation()

  // RN-07.12 / S92: an embedded BOM never gets this notice, whatever the link says.
  // Corrección PO 2026-09-10: nor does a workflow whose only newer versions are DRAFT or PROPOSAL —
  // `update` is empty then, and `link.updateAvailable` (index-blind, D19) is not enough on its own.
  if (!link.linked || !link.updateAvailable || !update.bomId) {
    return null
  }

  return (
    <Alert
      severity='info'
      data-testid='bom-update-banner'
      action={
        <Stack direction='row' spacing={1}>
          <Button variant='outlined' size='small' onClick={onUpdate} disabled={busy} data-testid='bom-update-open-impact'>
            {t_('Update')}
          </Button>
          <Button variant='outlined' size='small' color='inherit' onClick={onDismiss} disabled={busy} data-testid='bom-update-dismiss'>
            {t_('Dismiss')}
          </Button>
        </Stack>
      }
    >
      {t_('Version {{version}} of {{code}} is available', { version: update.versionNumber, code: link.code }) +
        ' (' + t_('linked: {{version}}', { version: link.versionNumber }) + ')'}
    </Alert>
  )
}

export default BomUpdateBanner
